import React, { useEffect, useState } from "react";
import { useApp } from "@/lib/store";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import {
    Star,
    MessageSquare,
    TrendingUp,
    ShieldAlert,
    Inbox,
    Sparkles,
    RefreshCw,
    ArrowRight,
    Loader2,
    Zap,
    Bell,
    Radar,
    X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
    LineChart,
    Line,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

function KpiCard({ testid, label, value, sub, icon: Icon, accent = "indigo" }) {
    const accents = {
        indigo: "from-indigo-500/20 to-indigo-500/5 text-indigo-300",
        emerald: "from-emerald-500/20 to-emerald-500/5 text-emerald-300",
        amber: "from-amber-500/20 to-amber-500/5 text-amber-300",
        cyan: "from-cyan-500/20 to-cyan-500/5 text-cyan-300",
        rose: "from-rose-500/20 to-rose-500/5 text-rose-300",
    };
    return (
        <div
            data-testid={testid}
            className="surface rounded-2xl p-5 relative overflow-hidden group"
        >
            <div
                className={`absolute inset-x-0 top-0 h-24 bg-gradient-to-b ${accents[accent]} opacity-70 pointer-events-none`}
            />
            <div className="relative flex items-start justify-between">
                <div>
                    <div className="label-eyebrow">{label}</div>
                    <div className="mt-3 text-3xl font-extrabold text-slate-50 tracking-tight">
                        {value}
                    </div>
                    {sub && (
                        <div className="mt-1 text-xs text-slate-400">{sub}</div>
                    )}
                </div>
                <div
                    className={`h-9 w-9 rounded-xl bg-white/5 flex items-center justify-center ${accents[accent].split(" ").pop()}`}
                >
                    <Icon className="h-4 w-4" />
                </div>
            </div>
        </div>
    );
}

export default function Home() {
    const { currentBusiness, featureFlags } = useApp();
    const nav = useNavigate();
    const [briefing, setBriefing] = useState(null);
    const [kpis, setKpis] = useState([]);
    const [googleReviewStats, setGoogleReviewStats] = useState(null);
    const [openComplaints, setOpenComplaints] = useState(0);
    const [pendingApprovals, setPendingApprovals] = useState(0);
    const [loading, setLoading] = useState(true);
    const [regen, setRegen] = useState(false);
    const [alerts, setAlerts] = useState([]);
    const [monBusy, setMonBusy] = useState(false);
    const [presence, setPresence] = useState(null);

    const monitoringEnabled = featureFlags?.monitoring !== false;
    // Derive unread from alerts state so dismiss + refetch never race.
    const alertsUnread = alerts.filter((a) => !a.read).length;

    const load = async () => {
        if (!currentBusiness) return;
        setLoading(true);
        try {
            // Each panel loads independently: a module switched off by an admin
            // (403) or a slow endpoint must not blank the whole dashboard.
            const [br, kp, ap, al, pr] = await Promise.allSettled([
                api.briefing(currentBusiness.id),
                api.kpis(currentBusiness.id),
                featureFlags?.approvals === false
                    ? Promise.resolve({ approvals: [] })
                    : api.approvals(currentBusiness.id, "pending"),
                monitoringEnabled
                    ? api.alerts(currentBusiness.id, { limit: 10 })
                    : Promise.resolve({ alerts: [] }),
                api.presenceLatest(currentBusiness.id),
            ]);
            const val = (r) => (r.status === "fulfilled" ? r.value : null);
            setBriefing(val(br)?.briefing || null);
            setKpis(val(kp)?.kpis || []);
            setGoogleReviewStats(val(kp)?.google_review_stats ?? null);
            setOpenComplaints(val(kp)?.open_complaints || 0);
            setPendingApprovals(val(ap)?.approvals?.length || 0);
            setAlerts(val(al)?.alerts || []);
            setPresence(val(pr)?.analysis || null);
            if (br.status === "rejected" && kp.status === "rejected") {
                toast.error("Couldn't load your dashboard.");
            }
        } finally {
            setLoading(false);
        }
    };

    const runMonitoring = async () => {
        if (!currentBusiness) return;
        setMonBusy(true);
        try {
            const r = await api.runMonitoring(currentBusiness.id);
            if (r.alerts_created > 0) {
                toast.success(`${r.alerts_created} new alert(s) generated.`);
            } else {
                toast.info("No significant changes detected.");
            }
            const alertData = await api.alerts(currentBusiness.id, { limit: 10 });
            setAlerts(alertData?.alerts || []);
        } catch {
            toast.error("Monitoring sync failed.");
        } finally {
            setMonBusy(false);
        }
    };

    const dismissAlert = async (aid) => {
        // Optimistic local mark-read; server call fires-and-forgets.
        setAlerts((prev) =>
            prev.map((a) => (a.id === aid ? { ...a, read: true } : a)),
        );
        try {
            await api.markAlertRead(aid);
        } catch {}
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentBusiness?.id]);

    const regenBriefing = async () => {
        setRegen(true);
        try {
            const { briefing: b } = await api.regenBriefing(currentBusiness.id);
            setBriefing(b);
            toast.success("Fresh briefing generated.");
        } catch {
            toast.error("Regeneration failed.");
        } finally {
            setRegen(false);
        }
    };

    const latest = kpis[kpis.length - 1] || {};
    const prev = kpis[kpis.length - 2] || {};
    const ratingDelta = ((latest.rating || 0) - (prev.rating || 0)).toFixed(2);
    const sentimentPct = Math.round(((latest.sentiment_score || 0) + 1) * 50);

    return (
        <div className="max-w-7xl mx-auto space-y-8 fade-in" data-testid="home-page">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="label-eyebrow text-indigo-300">
                        Daily briefing · {new Date().toLocaleDateString(undefined, {
                            weekday: "long",
                            month: "short",
                            day: "numeric",
                        })}
                    </div>
                    <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold text-slate-50 tracking-tight">
                        Good morning
                        {currentBusiness ? ` — ${currentBusiness.name}` : ""}
                    </h1>
                    <p className="mt-2 text-sm text-slate-400 max-w-2xl">
                        Here's what happened, why it matters, and what to do next.
                    </p>
                </div>
                <Button
                    data-testid="briefing-regenerate-button"
                    onClick={regenBriefing}
                    disabled={regen}
                    variant="outline"
                    className="border-white/10 bg-white/[0.03] hover:bg-white/[0.08] text-slate-200 gap-2"
                >
                    {regen ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        <RefreshCw className="h-4 w-4" />
                    )}
                    Regenerate briefing
                </Button>
            </div>

            {/* KPI grid */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
                <KpiCard
                    testid="kpi-card-rating"
                    label="Rating"
                    value={
                        googleReviewStats?.average_rating != null
                            ? Number(googleReviewStats.average_rating).toFixed(2)
                            : latest.rating?.toFixed?.(2) || "—"
                    }
                    sub={
                        googleReviewStats?.average_rating != null
                            ? `${googleReviewStats.total_reviews} Google reviews`
                            : kpis.length >= 2
                                ? `Δ ${ratingDelta} week over week`
                                : "No verified rating available"
                    }
                    icon={Star}
                    accent={ratingDelta >= 0 ? "emerald" : "rose"}
                />
                <KpiCard
                    testid="kpi-card-reviews"
                    label="Reviews this week"
                    value={
                        googleReviewStats?.reviews_this_week ??
                        (kpis.length ? latest.review_count ?? 0 : "—")
                    }
                    sub={
                        googleReviewStats != null
                            ? `${googleReviewStats.total_reviews} total Google reviews`
                            : "Weekly review data unavailable"
                    }
                    icon={MessageSquare}
                    accent="indigo"
                />
                <KpiCard
                    testid="kpi-card-sentiment"
                    label="Sentiment"
                    value={
                        googleReviewStats?.positive_sentiment_pct != null
                            ? `${googleReviewStats.positive_sentiment_pct}%`
                            : "—"
                    }
                    sub={
                        googleReviewStats?.analysed_count > 0
                            ? `${googleReviewStats.analysed_count} reviews analyzed`
                            : "Not analyzed yet"
                    }
                    icon={TrendingUp}
                    accent="cyan"
                />
                <KpiCard
                    testid="kpi-card-visibility"
                    label="Visibility score"
                    value={
                        kpis.length > 0 &&
                        latest.visibility_score != null
                            ? latest.visibility_score
                            : "—"
                    }
                    sub={
                        kpis.length > 0 &&
                        latest.visibility_score != null
                            ? "Google local ranking proxy"
                            : "Visibility data not available"
                    }
                    icon={Zap}
                    accent="amber"
                />
                <KpiCard
                    testid="kpi-card-complaints"
                    label="Open complaints"
                    value={openComplaints}
                    sub={`${pendingApprovals} approvals pending`}
                    icon={ShieldAlert}
                    accent={openComplaints > 0 ? "rose" : "emerald"}
                />
            </div>

            {/* Online presence */}
            <div
                className="surface rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4"
                data-testid="home-presence-card"
            >
                <div className="h-12 w-12 shrink-0 rounded-xl bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center text-cyan-300">
                    <Radar className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="label-eyebrow text-cyan-300">Online presence</div>
                    {presence?.status === "done" ? (
                        <>
                            <div className="mt-1 text-lg font-bold text-slate-50 truncate">
                                {presence.ai?.headline ||
                                    `${presence.coverage?.found ?? 0} of ${presence.coverage?.total ?? 5} profiles found`}
                            </div>
                            <div className="mt-0.5 text-xs text-slate-400">
                                {presence.coverage?.found ?? 0} of {presence.coverage?.total ?? 5} profiles
                                found · checked{" "}
                                {new Date(presence.finished_at || presence.created_at).toLocaleDateString()}
                            </div>
                        </>
                    ) : presence && ["queued", "running"].includes(presence.status) ? (
                        <div className="mt-1 text-sm text-slate-200 flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" /> Analysis running — your
                            profiles are being checked now.
                        </div>
                    ) : (
                        <div className="mt-1 text-sm text-slate-300">
                            {presence?.status === "failed"
                                ? "The last analysis didn't finish. Run it again to see how you look online."
                                : "Check how your Google, website, Instagram, Facebook and LinkedIn look to customers."}
                        </div>
                    )}
                </div>
                {presence?.status === "done" && presence.overall_score != null && (
                    <div className="text-right shrink-0" data-testid="home-presence-score">
                        <div className="text-3xl font-extrabold text-slate-50 tracking-tight">
                            {presence.overall_score}
                        </div>
                        <div className="text-[11px] text-slate-400">out of 100</div>
                    </div>
                )}
                <Button
                    data-testid="home-presence-open"
                    onClick={() => nav("/presence")}
                    className="shrink-0 bg-indigo-500 hover:bg-indigo-400 text-white gap-2"
                >
                    {presence?.status === "done" ? "View analysis" : "Run analysis"}
                    <ArrowRight className="h-4 w-4" />
                </Button>
            </div>

            {monitoringEnabled && (
                <div
                    className="surface rounded-2xl p-5"
                    data-testid="alerts-feed"
                >
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                            <div className="h-9 w-9 rounded-xl bg-rose-500/15 border border-rose-500/30 flex items-center justify-center">
                                <Bell className="h-4 w-4 text-rose-300" />
                            </div>
                            <div>
                                <div className="label-eyebrow text-rose-300">
                                    Continuous monitoring
                                </div>
                                <h3 className="text-lg font-bold text-slate-100">
                                    Alerts feed
                                    {alertsUnread > 0 && (
                                        <span
                                            data-testid="alerts-unread-count"
                                            className="ml-2 inline-flex items-center rounded-full bg-rose-500/20 border border-rose-500/40 text-rose-100 text-[10px] font-bold px-2 py-0.5"
                                        >
                                            {alertsUnread} new
                                        </span>
                                    )}
                                </h3>
                            </div>
                        </div>
                        <Button
                            data-testid="monitoring-sync-button"
                            onClick={runMonitoring}
                            disabled={monBusy}
                            variant="outline"
                            className="border-white/10 bg-white/[0.03] hover:bg-white/[0.08] text-slate-200 gap-2"
                        >
                            {monBusy ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Radar className="h-4 w-4" />
                            )}
                            Sync now
                        </Button>
                    </div>
                    {alerts.length === 0 ? (
                        <div className="text-sm text-slate-500 py-6 text-center">
                            No alerts yet — trigger a sync to check for changes.
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {alerts.slice(0, 6).map((a) => (
                                <AlertCard
                                    key={a.id}
                                    alert={a}
                                    onDismiss={() => dismissAlert(a.id)}
                                    nav={nav}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Briefing + chart */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div
                    data-testid="ai-briefing-card"
                    className="lg:col-span-2 surface rounded-2xl p-6"
                >
                    {loading || !briefing ? (
                        <div className="h-40 flex items-center justify-center text-slate-500 gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" /> Preparing
                            your briefing…
                        </div>
                    ) : (
                        <>
                            <div className="flex items-start gap-3">
                                <div className="h-10 w-10 rounded-xl bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center">
                                    <Sparkles className="h-5 w-5 text-indigo-300" />
                                </div>
                                <div className="flex-1">
                                    <div className="label-eyebrow text-indigo-300">
                                        AI Manager · What happened?
                                    </div>
                                    <h2
                                        data-testid="briefing-headline"
                                        className="mt-1 text-xl font-bold text-slate-50 tracking-tight"
                                    >
                                        {briefing.headline}
                                    </h2>
                                    <p
                                        data-testid="briefing-narrative"
                                        className="mt-2 text-sm text-slate-300 leading-relaxed"
                                    >
                                        {briefing.narrative}
                                    </p>
                                </div>
                            </div>

                            {briefing.highlights?.length > 0 && (
                                <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    {briefing.highlights.slice(0, 3).map((h, i) => (
                                        <div
                                            key={i}
                                            data-testid={`briefing-highlight-${i}`}
                                            className="rounded-xl bg-white/[0.03] border border-white/5 p-3 text-xs text-slate-300"
                                        >
                                            {h}
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="mt-6">
                                <div className="label-eyebrow text-indigo-300 mb-3">
                                    What should I do?
                                </div>
                                <div className="space-y-3">
                                    {(briefing.recommendations || []).map((r, i) => (
                                        <RecCard key={i} rec={r} idx={i} nav={nav} />
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                <div className="surface rounded-2xl p-5">
                    <div className="label-eyebrow text-cyan-300 mb-2">
                        Rating trend · 10 weeks
                    </div>
                    <div className="h-40">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart
                                data={kpis.map((k) => ({
                                    ...k,
                                    label: k.week_start.slice(5),
                                }))}
                            >
                                <XAxis
                                    dataKey="label"
                                    stroke="#475569"
                                    fontSize={10}
                                    tickLine={false}
                                    axisLine={false}
                                />
                                <YAxis
                                    domain={[3.5, 5]}
                                    stroke="#475569"
                                    fontSize={10}
                                    tickLine={false}
                                    axisLine={false}
                                />
                                <Tooltip
                                    contentStyle={{
                                        background: "#0b0f19",
                                        border: "1px solid rgba(255,255,255,0.08)",
                                        borderRadius: 10,
                                        color: "#e2e8f0",
                                        fontSize: 12,
                                    }}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="rating"
                                    stroke="#6366F1"
                                    strokeWidth={2}
                                    dot={{ r: 3, fill: "#6366F1" }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="mt-4 space-y-2">
                        <div className="flex items-center justify-between text-xs text-slate-400">
                            <span>Positive review share</span>
                            <span className="text-emerald-300 font-mono">
                                {sentimentPct}%
                            </span>
                        </div>
                        <div className="h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
                            <div
                                className="h-full bg-gradient-to-r from-emerald-500 to-cyan-400"
                                style={{ width: `${sentimentPct}%` }}
                            />
                        </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-white/5 grid grid-cols-2 gap-2 text-xs">
                        <button
                            onClick={() => nav("/approvals")}
                            data-testid="quick-approvals"
                            className="rounded-xl bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/20 text-amber-200 font-semibold py-2 flex items-center justify-center gap-1.5"
                        >
                            <Inbox className="h-3.5 w-3.5" /> {pendingApprovals} pending
                        </button>
                        <button
                            onClick={() => nav("/reputation")}
                            data-testid="quick-reputation"
                            className="rounded-xl bg-indigo-500/10 hover:bg-indigo-500/20 border border-indigo-500/20 text-indigo-200 font-semibold py-2 flex items-center justify-center gap-1.5"
                        >
                            <Star className="h-3.5 w-3.5" /> Review inbox
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function RecCard({ rec, idx, nav }) {
    const actionMap = {
        approve: {
            label: rec.action_label || "Open Approvals",
            testid: `briefing-action-approve-${idx}`,
            onClick: () => nav("/approvals"),
            className:
                "bg-amber-500/15 hover:bg-amber-500/25 border-amber-500/30 text-amber-200",
        },
        ask_ai: {
            label: rec.action_label || "Ask AI",
            testid: `briefing-action-ask-${idx}`,
            onClick: () => nav("/ai-manager"),
            className:
                "bg-indigo-500/15 hover:bg-indigo-500/25 border-indigo-500/30 text-indigo-200",
        },
        do_it: {
            label: rec.action_label || "Do it",
            testid: `briefing-action-do-${idx}`,
            onClick: () => nav("/reputation"),
            className:
                "bg-emerald-500/15 hover:bg-emerald-500/25 border-emerald-500/30 text-emerald-200",
        },
    };
    const cfg = actionMap[rec.action_kind] || actionMap.ask_ai;
    return (
        <div
            data-testid={`briefing-recommendation-${idx}`}
            className="rounded-xl bg-white/[0.02] border border-white/5 p-4 flex items-start justify-between gap-4"
        >
            <div>
                <div className="text-sm font-semibold text-slate-100">
                    {rec.title}
                </div>
                <div className="mt-1 text-xs text-slate-400 leading-relaxed">
                    {rec.body}
                </div>
            </div>
            <button
                onClick={cfg.onClick}
                data-testid={cfg.testid}
                className={`shrink-0 rounded-xl border px-3 py-1.5 text-xs font-semibold flex items-center gap-1.5 transition-colors ${cfg.className}`}
            >
                {cfg.label}
                <ArrowRight className="h-3 w-3" />
            </button>
        </div>
    );
}

function AlertCard({ alert, onDismiss, nav }) {
    const severityMap = {
        critical: "border-rose-500/40 bg-rose-500/10 text-rose-100",
        high: "border-rose-500/30 bg-rose-500/[0.06] text-rose-100",
        medium: "border-amber-500/30 bg-amber-500/[0.06] text-amber-100",
        low: "border-cyan-500/30 bg-cyan-500/[0.06] text-cyan-100",
    };
    // Client-side fallback map — mirrors backend _ACTION_MAP so legacy alerts
    // still render a working primary action button.
    const KIND_FALLBACK = {
        rating_drop:         { can_ai_fix: true,  action_target: "reputation", action_label: "Draft replies with AI" },
        rating_up:           { can_ai_fix: false, action_target: "reputation", action_label: "Review new 5★ reviews" },
        new_reviews:         { can_ai_fix: true,  action_target: "approvals",  action_label: "Review AI drafts" },
        visibility_drop:     { can_ai_fix: true,  action_target: "audit",      action_label: "Run AI audit fix" },
        engagement_negative: { can_ai_fix: true,  action_target: "reputation", action_label: "Moderate with AI" },
        complaints_up:       { can_ai_fix: true,  action_target: "approvals",  action_label: "Open Approvals inbox" },
    };
    const fb = KIND_FALLBACK[alert.kind] || {};
    const canAiFix = alert.can_ai_fix != null ? alert.can_ai_fix : !!fb.can_ai_fix;
    const actionTarget = alert.action_target && alert.action_target !== "none"
        ? alert.action_target
        : fb.action_target;
    const actionLabel = alert.action_label || fb.action_label || (canAiFix ? "Fix with AI" : "Review");
    const targetPath = {
        reputation: "/reputation",
        approvals: "/approvals",
        audit: "/audit",
        reports: "/reports",
        ai_manager: "/ai-manager",
    }[actionTarget];

    return (
        <div
            data-testid={`alert-card-${alert.id}`}
            className={`rounded-xl border p-4 ${severityMap[alert.severity] || severityMap.low} ${alert.read ? "opacity-60" : ""}`}
        >
            <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span
                            data-testid={`alert-severity-${alert.id}`}
                            className="label-eyebrow"
                        >
                            {alert.severity}
                        </span>
                        {canAiFix ? (
                            <span
                                data-testid={`alert-can-ai-fix-${alert.id}`}
                                className="inline-flex items-center gap-1 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-100 text-[10px] font-bold px-2 py-0.5"
                            >
                                <Sparkles className="h-2.5 w-2.5" /> AI can fix
                            </span>
                        ) : (
                            <span
                                data-testid={`alert-needs-you-${alert.id}`}
                                className="inline-flex items-center gap-1 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-100 text-[10px] font-bold px-2 py-0.5"
                            >
                                Needs you
                            </span>
                        )}
                        <span className="text-[10px] mono uppercase text-slate-500">
                            {alert.metric} · {alert.delta}
                        </span>
                        {alert.read && (
                            <span className="text-[10px] mono uppercase text-slate-500">
                                read
                            </span>
                        )}
                    </div>
                    <div className="mt-1 text-sm font-semibold">
                        {alert.title}
                    </div>
                    <div className="mt-1 text-xs opacity-80">
                        {alert.what_changed}
                    </div>
                    {alert.why && (
                        <div className="mt-1 text-xs italic opacity-70">
                            {alert.why}
                        </div>
                    )}
                    {alert.actions?.length > 0 && (
                        <ul className="mt-2 space-y-0.5 text-xs">
                            {alert.actions.map((a, i) => (
                                <li key={i}>· {a}</li>
                            ))}
                        </ul>
                    )}
                    <div className="mt-3 flex items-center gap-2">
                        <button
                            data-testid={`alert-action-${alert.id}`}
                            onClick={() => {
                                if (!alert.read) onDismiss?.();
                                if (targetPath) nav(targetPath);
                            }}
                            className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold border transition-colors ${
                                canAiFix
                                    ? "bg-emerald-500/15 hover:bg-emerald-500/25 border-emerald-500/40 text-emerald-100"
                                    : "bg-white/[0.06] hover:bg-white/[0.12] border-white/15 text-slate-100"
                            }`}
                        >
                            {canAiFix && <Sparkles className="h-3 w-3" />}
                            {actionLabel}
                            <ArrowRight className="h-3 w-3" />
                        </button>
                        {!alert.read && (
                            <button
                                onClick={onDismiss}
                                data-testid={`alert-dismiss-${alert.id}`}
                                className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] text-slate-400 hover:text-slate-200 hover:bg-white/[0.05] border border-transparent hover:border-white/10"
                            >
                                Mark as read
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
